import { Component, computed, effect, Signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngxs/store';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest, distinctUntilChanged } from 'rxjs';
import {
    ApexAxisChartSeries,
    ApexDataLabels,
    ApexTooltip,
    ApexXAxis,
    ApexYAxis,
} from 'ng-apexcharts';
import { PathOptions } from 'leaflet';

import { GenericComponent } from '../generic/generic.component';
import { CC_CONSTANTS } from '../../assets/constants';
import { ConcessioncontrolState, PrepareDataFromKeys } from '../../states/concessioncontrol.state';
import { SidenavService } from '../../../../../shared/services/sidenav.service';
import {
    colorAec1BlueLight,
    colorAec2BlueDark,
    colorAec3Green,
    colorAec4Orange,
    colorAec7RedLight,
    colorAec8RedDark,
    colorBlack,
} from '../../../../../shared/all.constants';
import { convertNumber } from '../../../../../shared/all.helpers';
import { LegendConfig } from '../../../../../shared/components/map/map.component';
import {
    DefaultPolygonStyle,
    EmptyPolygonStyle,
    HatchImage,
    MapColors,
} from '../../../../../shared/components/map/map.constants';
import {
    MV_NETWORK_OMT_EVOLUTION_CHART,
    MV_NETWORK_RENEWAL_RATE_CHART,
    MV_NETWORK_TYPE_EVOLUTION_CHART,
    MV_NETWORKS_UNDERGROUNDING_KPIS,
    MV_NETWORKS_UNDERGROUNDING_RATE_CONCESSION_CHART,
    MV_NETWORKS_UNDERGROUNDING_RATE_DEP_CHART,
    MV_UNDERGROUNDING_RATE_MAP,
} from '../../assets/keys';

const KEYS = [
    MV_NETWORKS_UNDERGROUNDING_KPIS,
    MV_NETWORKS_UNDERGROUNDING_RATE_DEP_CHART,
    MV_NETWORKS_UNDERGROUNDING_RATE_CONCESSION_CHART,
    MV_UNDERGROUNDING_RATE_MAP,
    MV_NETWORK_TYPE_EVOLUTION_CHART,
    MV_NETWORK_RENEWAL_RATE_CHART,
    MV_NETWORK_OMT_EVOLUTION_CHART,
];

interface KpiData {
    //Minimum of a line
    organizationId?: string;
    year?: string;
    error?: boolean;

    totalMvNetworkLength?: number;
    totalMvNetworkLengthEvolution?: number;
    mvBurialRate?: number;
    mvBurialRateEvolution?: number;
    mvFeedersCount?: number;
    mvFeedersCountEvolution?: number;
    mvFeedersOver70kmCount?: number;
    nbMVFeederLongestName?: string;
    nbMVFeederLongestLength?: number;
}

interface ChartData {
    organizationId?: string;
    year?: string;
    xDensityUsersPerKmNetwork?: number;
    yUndergroundRate?: number;
    error?: boolean;
    departmentCode?: string;
    departmentName?: string;
}

interface NetworkTypeChartData {
    organizationId?: string;
    year?: string;
    xYear?: number;
    mvUndergroundRate?: number;
    mvAerialRate?: number;
    mvNetworkLength?: number;
    error?: boolean;
}

interface RenewalRateChartData {
    organizationId?: string;
    year?: string;
    xYear?: number;
    reliablePdvLength?: number;
    treatedPdvLength?: number;
    rpLength?: number;
    pdvRate?: number;
    error?: boolean;
}

interface NetworkOmtChartData {
    organizationId?: string;
    year?: string;
    xYear?: number;
    omtTotalCount?: number;
    omtMeanCount?: number;
    error?: boolean;
}

@UntilDestroy()
@Component({
    selector: 'app-mv-networks-undergrounding',
    templateUrl: './mv-networks-undergrounding.component.html',
    styleUrls: ['./mv-networks-undergrounding.component.scss'],
})
export class MvNetworksUndergroundingComponent extends GenericComponent {
    public kpis: Signal<KpiData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(MV_NETWORKS_UNDERGROUNDING_KPIS),
    ) as Signal<KpiData[]>;

    public texts = CC_CONSTANTS.mvNetworks.tabs.undergrounding.texts;

    public chartTitle = this.texts.mvUndergroundRateChart.title;
    public chartSeries: ApexAxisChartSeries = [];
    public networkTypeChartSeries: ApexAxisChartSeries = [];
    public networkRenewalRateChartSeries: ApexAxisChartSeries = [];
    public networkOmtChartSeries: ApexAxisChartSeries = [];
    public dataLabels: ApexDataLabels = { enabled: false, enabledOnSeries: [] };
    public networkTypeDataLabels: ApexDataLabels = { enabled: true, enabledOnSeries: [] };
    public networkRenewalRateDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: this.setDataLabelsFormatterBySerie({ 0: null, 1: null, 2: null, 3: null }),
    };
    public networkOmtDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: this.setDataLabelsFormatterBySerie({ 0: null, 1: null }),
    };
    public errorChart: boolean = true;
    public networkTypeXaxisCategories: ApexXAxis['categories'];
    public networkRenewalRateXaxisCategories: ApexXAxis['categories'];
    public networkOmtXaxisCategories: ApexXAxis['categories'];
    public networkTypeChartError: boolean = true;
    public networkRenewalRateChartError: boolean = true;
    public networkOmtChartError: boolean = true;
    public mvNetworkTypeEvolutionCustomTooltip: ApexTooltip['custom'];
    public mvNetworkRenewalRateCustomTooltip: ApexTooltip['custom'];

    public chartDataDep: Signal<ChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(MV_NETWORKS_UNDERGROUNDING_RATE_DEP_CHART),
    ) as Signal<ChartData[]>;

    public chartDataConcession: Signal<ChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(MV_NETWORKS_UNDERGROUNDING_RATE_CONCESSION_CHART),
    ) as Signal<ChartData[]>;

    public chartNetworkType: Signal<NetworkTypeChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(MV_NETWORK_TYPE_EVOLUTION_CHART),
    ) as Signal<NetworkTypeChartData[]>;

    public chartNetworkRenewalRate: Signal<RenewalRateChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(MV_NETWORK_RENEWAL_RATE_CHART),
    ) as Signal<RenewalRateChartData[]>;

    public chartNetworkOmt: Signal<NetworkOmtChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(MV_NETWORK_OMT_EVOLUTION_CHART),
    ) as Signal<NetworkOmtChartData[]>;

    public yaxis: ApexYAxis[] = [
        {
            seriesName: 'Axix left',
            min: 0,
            max: 100,
            title: {
                text: this.texts.mvUndergroundRateChart.yaxisTitle,
            },
        },
    ];
    public networkTypeYaxis: ApexYAxis[] = [
        {
            seriesName: [
                this.texts.mvNetworkTypeEvolutionChart.aerialRateAxisTitle,
                this.texts.mvNetworkTypeEvolutionChart.undergroundRateAxisTitle,
            ],
            min: 0,
            max: 100,
            title: {
                text: this.texts.mvNetworkTypeEvolutionChart.yaxisTitleLeft,
            },
        },
        {
            seriesName: this.texts.mvNetworkTypeEvolutionChart.networkLengthAxisTitle,
            min: 0,
            opposite: true,
            title: {
                text: this.texts.mvNetworkTypeEvolutionChart.yaxisTitleRight,
            },
        },
    ];
    public networkOmtYaxis: ApexYAxis[] = [
        {
            seriesName: this.texts.mvNetworkOmtEvolutionChart.omtMeanAxisTitle,
            min: 0,
            opposite: true,
            title: {
                text: this.texts.mvNetworkOmtEvolutionChart.yaxisTitleRight,
            },
        },
        {
            seriesName: this.texts.mvNetworkOmtEvolutionChart.omtTotalAxisTitle,
            min: 0,
            title: {
                text: this.texts.mvNetworkOmtEvolutionChart.yaxisTitleLeft,
            },
        },
    ];
    public networkRenewalRateYaxis: ApexYAxis[] = [
        {
            seriesName: [
                this.texts.mvNetworkRenewalRateChart.reliablePdvLengthTitle,
                this.texts.mvNetworkRenewalRateChart.treatedPdvLengthTitle,
                this.texts.mvNetworkRenewalRateChart.rpLengthTitle,
            ],
            min: 0,
            title: {
                text: this.texts.mvNetworkRenewalRateChart.yaxisTitleLeft,
            },
        },
        {
            seriesName: this.texts.mvNetworkRenewalRateChart.pdvRateTitle,
            min: 0,
            opposite: true,
            title: {
                text: this.texts.mvNetworkRenewalRateChart.yaxisTitleRight,
            },
        },
    ];

    public mvUndergroundingRateCustomTooltip: ApexTooltip['custom'];
    public networkRenewalRateCustomTooltip: ApexTooltip['custom'];
    public networkOmtCustomTooltip: ApexTooltip['custom'];

    public geometries: Signal<any[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(MV_UNDERGROUNDING_RATE_MAP),
    ) as Signal<any[]>;
    public legendConfig: LegendConfig[] = [
        {
            title: "Taux d'enfouissement HTA",
            values: [
                { color: MapColors.red, value: '0 à 1%' },
                { color: MapColors.orange, value: '1 à 25%' },
                { color: MapColors.yellow, value: '25 à 50%' },
                { color: MapColors.paleGreen, value: '50 à 75%' },
                { color: MapColors.lightGreen, value: '75 à 99%' },
                { color: MapColors.green, value: '99 à 100%' },
                { color: `url(${HatchImage})`, value: 'Communes hors concession' },
            ],
        },
    ];

    constructor(
        private readonly store: Store,
        private readonly route: ActivatedRoute,
        private readonly sidenavService: SidenavService,
    ) {
        //GenericComponent intialize sideNav, Tabs (if exists), title and subTitle
        super(route, sidenavService, store);

        effect(() => {
            const dataListConcession = this.chartDataConcession();
            const dataListDep = this.chartDataDep();
            const dataListNetworkType = this.chartNetworkType();
            const dataListNetworkOmt = this.chartNetworkOmt();
            const dataListNetworkRenewalRate = this.chartNetworkRenewalRate();

            const concessionError = this.checkForError(dataListConcession, [
                'xDensityUsersPerKmNetwork',
                'yUndergroundRate',
            ]);
            const depError = this.checkForError(dataListDep, [
                'xDensityUsersPerKmNetwork',
                'yUndergroundRate',
            ]);
            this.networkTypeChartError = this.checkForError(dataListNetworkType, [
                'xYear',
                'mvUndergroundRate',
                'mvAerialRate',
                'mvNetworkLength',
            ]);
            this.networkOmtChartError = this.checkForError(dataListNetworkOmt, [
                'xYear',
                'omtTotalCount',
                'omtMeanCount',
            ]);
            this.networkRenewalRateChartError = this.checkForError(dataListNetworkRenewalRate, [
                'xYear',
                'reliablePdvLength',
                'treatedPdvLength',
                'rpLength',
                'pdvRate',
            ]);
            this.errorChart = concessionError || depError;

            if (!this.errorChart) {
                this.chartSeries = [
                    {
                        name: this.texts.mvUndergroundRateChart
                            .mvUndergroundRateDepartmentAxisTitle,
                        type: 'scatter',
                        data: dataListDep.map((data) => ({
                            x: data.xDensityUsersPerKmNetwork,
                            y: data.yUndergroundRate,
                            departmentCode: data.departmentCode,
                            departmentName: data.departmentName,
                        })),
                        color: colorAec1BlueLight,
                    },
                    {
                        name: this.texts.mvUndergroundRateChart
                            .mvUndergroundRateConcessionAxisTitle,
                        type: 'scatter',
                        data: dataListConcession.map((data) => ({
                            x: data.xDensityUsersPerKmNetwork,
                            y: data.yUndergroundRate,
                        })),
                        color: colorAec8RedDark,
                    },
                ];
            }

            if (!this.networkTypeChartError) {
                this.networkTypeXaxisCategories = dataListNetworkType.map((data) => data.xYear);
                this.networkTypeChartSeries = [
                    {
                        name: this.texts.mvNetworkTypeEvolutionChart.undergroundRateAxisTitle,
                        type: 'column',
                        data: dataListNetworkType.map((data) => data.mvUndergroundRate!),
                        color: colorAec1BlueLight,
                    },
                    {
                        name: this.texts.mvNetworkTypeEvolutionChart.aerialRateAxisTitle,
                        type: 'column',
                        data: dataListNetworkType.map((data) => data.mvAerialRate!),
                        color: colorAec3Green,
                    },
                    {
                        name: this.texts.mvNetworkTypeEvolutionChart.networkLengthAxisTitle,
                        type: 'line',
                        data: dataListNetworkType.map((data) => data.mvNetworkLength!),
                        color: colorAec7RedLight,
                    },
                ];
                this.networkTypeDataLabels.enabledOnSeries = [0, 1, 2];
            }

            if (!this.networkOmtChartError) {
                this.networkOmtXaxisCategories = dataListNetworkOmt.map((data) => data.xYear);
                this.networkOmtChartSeries = [
                    {
                        name: this.texts.mvNetworkOmtEvolutionChart.omtTotalAxisTitle,
                        type: 'column',
                        data: dataListNetworkOmt.map((data) => data.omtTotalCount!),
                        color: colorAec2BlueDark,
                    },
                    {
                        name: this.texts.mvNetworkOmtEvolutionChart.omtMeanAxisTitle,
                        type: 'line',
                        data: dataListNetworkOmt.map((data) => data.omtMeanCount!),
                        color: colorAec7RedLight,
                    },
                ];
                this.networkOmtDataLabels.enabledOnSeries = [0, 1, 2];
            }

            if (!this.networkRenewalRateChartError) {
                this.networkRenewalRateXaxisCategories = dataListNetworkRenewalRate.map(
                    (data) => data.xYear,
                );
                this.networkRenewalRateChartSeries = [
                    {
                        name: this.texts.mvNetworkRenewalRateChart.treatedPdvLengthTitle,
                        type: 'column',
                        data: dataListNetworkRenewalRate.map((data) => data.treatedPdvLength!),
                        color: colorAec1BlueLight,
                    },
                    {
                        name: this.texts.mvNetworkRenewalRateChart.reliablePdvLengthTitle,
                        type: 'column',
                        data: dataListNetworkRenewalRate.map((data) => data.reliablePdvLength!),
                        color: colorAec3Green,
                    },
                    {
                        name: this.texts.mvNetworkRenewalRateChart.rpLengthTitle,
                        type: 'column',
                        data: dataListNetworkRenewalRate.map((data) => data.rpLength!),
                        color: colorAec4Orange,
                    },
                    {
                        name: this.texts.mvNetworkRenewalRateChart.pdvRateTitle,
                        type: 'line',
                        data: dataListNetworkRenewalRate.map((data) => data.pdvRate!),
                        color: colorAec8RedDark,
                    },
                ];
                this.networkRenewalRateDataLabels.enabledOnSeries = [0, 1, 2, 3];
            }
        });
    }

    ngOnInit() {
        // Every time currentOrganization or currentYear changes, we tell the store he has to ask for the data
        combineLatest([this._currentOrganization$, this._currentYear$])
            .pipe(untilDestroyed(this), distinctUntilChanged())
            .subscribe(() => {
                this.store.dispatch(new PrepareDataFromKeys({ keys: KEYS }));
            });

        this.networkRenewalRateCustomTooltip = this.setNetworkRenewalRateCustomTooltip();
        this.networkOmtCustomTooltip = this.setNetworkOmtCustomTooltip();
        this.mvNetworkTypeEvolutionCustomTooltip = this.setChartCustomTooltip(
            this.texts.mvNetworkTypeEvolutionChart.precision,
            this.texts.mvNetworkTypeEvolutionChart.units,
        );
        this.mvUndergroundingRateCustomTooltip = this.setMvUndergroundRateCustomTooltip();
    }

    public setNetworkRenewalRateCustomTooltip(): ApexTooltip['custom'] {
        return ({ data, seriesIndex, dataPointIndex, w }) => {
            const collapsedSeriesIndices = w.globals.collapsedSeriesIndices;

            const categoryLabels = w.globals.categoryLabels;
            const title = categoryLabels[dataPointIndex];

            const precision = this.texts.mvNetworkRenewalRateChart.precision;
            const ratePrecision = this.texts.mvNetworkRenewalRateChart.ratePrecision;

            const pdvReliableSerie = w.globals.initialSeries[0];
            const pdvReliableName = pdvReliableSerie.name;
            const pdvReliableColor = pdvReliableSerie.color;
            const pdvReliablePoint = pdvReliableSerie.data[dataPointIndex];
            const pdvReliableStringified = convertNumber(pdvReliablePoint, precision, false);

            const pdvTreatedSerie = w.globals.initialSeries[1];
            const pdvTreatedName = pdvTreatedSerie.name;
            const pdvTreatedColor = pdvTreatedSerie.color;
            const incomeTreatedPoint = pdvTreatedSerie.data[dataPointIndex];
            const pdvTreatedStringified = convertNumber(incomeTreatedPoint, precision, false);

            const rpTreatedSerie = w.globals.initialSeries[2];
            const rpTreatedName = rpTreatedSerie.name;
            const rpTreatedColor = rpTreatedSerie.color;
            const rpTreatedPoint = rpTreatedSerie.data[dataPointIndex];
            const rpTreatedStringified = convertNumber(rpTreatedPoint, precision, false);

            const rateSerie = w.globals.initialSeries[3];
            const rateName = rateSerie.name;
            const rateColor = rateSerie.color;
            const ratePoint = rateSerie.data[dataPointIndex];
            const rateStringified = convertNumber(ratePoint, ratePrecision, false);

            const dataTooltips = [
                {
                    color: pdvReliableColor,
                    name: pdvReliableName,
                    value: pdvReliableStringified + ' km',
                },
                {
                    color: pdvTreatedColor,
                    name: pdvTreatedName,
                    value: pdvTreatedStringified + ' km',
                },
                {
                    color: rpTreatedColor,
                    name: rpTreatedName,
                    value: rpTreatedStringified + ' km',
                },
                {
                    color: rateColor,
                    name: rateName,
                    value: rateStringified + ' %',
                },
            ];
            const html = this.setCustomTooltip(collapsedSeriesIndices, title, dataTooltips);
            return html;
        };
    }

    public setNetworkOmtCustomTooltip(): ApexTooltip['custom'] {
        return ({ data, seriesIndex, dataPointIndex, w }) => {
            const collapsedSeriesIndices = w.globals.collapsedSeriesIndices;

            const categoryLabels = w.globals.categoryLabels;
            const title = categoryLabels[dataPointIndex];

            const precision = this.texts.mvNetworkRenewalRateChart.precision;
            const ratePrecision = this.texts.mvNetworkRenewalRateChart.ratePrecision;

            const omtCountSerie = w.globals.initialSeries[0];
            const omtCountName = omtCountSerie.name;
            const omtCountColor = omtCountSerie.color;
            const omtCountPoint = omtCountSerie.data[dataPointIndex];
            const omtCountStringified = convertNumber(omtCountPoint, precision, false);

            const rateSerie = w.globals.initialSeries[1];
            const rateName = rateSerie.name;
            const rateColor = rateSerie.color;
            const ratePoint = rateSerie.data[dataPointIndex];
            const rateStringified = convertNumber(ratePoint, ratePrecision, false);

            const dataTooltips = [
                {
                    color: omtCountColor,
                    name: omtCountName,
                    value: omtCountStringified,
                },
                {
                    color: rateColor,
                    name: rateName,
                    value: rateStringified,
                },
            ];
            const html = this.setCustomTooltip(collapsedSeriesIndices, title, dataTooltips);
            return html;
        };
    }

    public getMapStyle(feature: GeoJSON.Feature | undefined): PathOptions {
        if (!feature?.properties || feature.properties['mvUndergroundingRate'] === null) {
            return EmptyPolygonStyle;
        }

        // Cities style
        let fillColor = MapColors.red;
        const mvUndergroundingRate = Number(feature.properties['mvUndergroundingRate']);
        if (mvUndergroundingRate > 99) {
            fillColor = MapColors.green;
        } else if (mvUndergroundingRate > 75) {
            fillColor = MapColors.lightGreen;
        } else if (mvUndergroundingRate > 50) {
            fillColor = MapColors.paleGreen;
        } else if (mvUndergroundingRate > 25) {
            fillColor = MapColors.yellow;
        } else if (mvUndergroundingRate > 1) {
            fillColor = MapColors.orange;
        }

        return {
            ...DefaultPolygonStyle,
            fillColor,
        };
    }

    public getTooltip(feature: GeoJSON.Feature): string {
        if (!feature.properties) {
            return '';
        }
        const { cityName, mvUndergroundingRate, isInConcession } = feature.properties;
        if (!isInConcession) {
            return `${cityName} - Hors concession`;
        }

        const valueStringified = convertNumber(mvUndergroundingRate);
        return `${cityName} - ${valueStringified} % d'enfouissement HTA`;
    }

    public setChartCustomTooltip(
        precision: number | null = null,
        units: string[] = [],
    ): ApexTooltip['custom'] {
        return function ({ series, seriesIndex, dataPointIndex, w }) {
            const year = w.globals.categoryLabels[dataPointIndex];
            const hiddenSeries = new Set(w.globals.collapsedSeriesIndices);

            let tooltipHtml = `<div class="apexcharts-tooltip-title">${year}</div>`;

            (
                w.globals.initialSeries as Array<{ name: string; color?: string; data: any[] }>
            ).forEach((serie, idx) => {
                if (hiddenSeries.has(idx)) return;
                const serieName = serie.name;
                const serieColor = serie.color || colorBlack;
                const dataPoint = serie.data[dataPointIndex];

                if (dataPoint !== undefined) {
                    const value =
                        precision !== null ? Number(dataPoint).toFixed(precision) : dataPoint;

                    const unit = units[idx] || '';

                    tooltipHtml += `
                        <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: ${idx}; display: flex; align-items: center;">
                            <span class="apexcharts-tooltip-marker" style="background-color: ${serieColor}; margin-right: 8px;"></span>
                            <div class="apexcharts-tooltip-text">
                                <div class="apexcharts-tooltip-y-group">
                                    <span class="apexcharts-tooltip-text-y-label">${serieName}:</span>
                                    <span class="apexcharts-tooltip-text-y-value">${convertNumber(value, precision, false)} ${unit}</span>
                                </div>
                            </div>
                        </div>
                    `;
                }
            });

            return tooltipHtml;
        };
    }

    public setMvUndergroundRateCustomTooltip(): ApexTooltip['custom'] {
        return ({ series, seriesIndex, dataPointIndex, w }) => {
            const precision = this.texts.mvUndergroundRateChart.precision;
            const serie = w.config.series[seriesIndex];

            const data = serie.data[dataPointIndex];
            const title =
                seriesIndex === 0
                    ? `${data.departmentCode} - ${data.departmentName}`
                    : 'Ma concession';

            const xaxisTitle = this.texts.mvUndergroundRateChart.tooltipXaxisTitle;
            const xValue = `${convertNumber(data.x, precision)} ${this.texts.mvUndergroundRateChart.tooltipXaxisUnit}`;

            const yaxisTitle = this.texts.mvUndergroundRateChart.tooltipYaxisTitle;
            const yValue = `${convertNumber(data.y, precision)} ${this.texts.mvUndergroundRateChart.tooltipYaxisUnit}`;

            const dataTooltips = [
                {
                    color: '',
                    name: xaxisTitle,
                    value: xValue,
                },
                {
                    color: '',
                    name: yaxisTitle,
                    value: yValue,
                },
            ];
            const html = this.setCustomTooltip([], title, dataTooltips);
            return html;
        };
    }
}
